import { useState, useContext, useEffect } from 'react';
import { Checkbox, FormControlLabel, TextField, Tooltip, Typography } from '@mui/material';
import CreateIcon from '@mui/icons-material/Create';
import DialogGlynt, { FormControlStyled, FormLabelStyled } from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';
import removeTrailingSpaces from '../../upload/util/removeTrailingSpaces';
import useGlynt from '../../hooks/useGlynt';
import { AppContext } from '../../../App';
import { AxiosError } from 'axios';
import { IExtractionBatch } from '../../../types/my-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LABEL_LIMIT } from '../../../constants';

interface IExtractionBatchRenameProps {
  extractionBatch: IExtractionBatch;
}

function ExtractionBatchRename({ extractionBatch }: IExtractionBatchRenameProps) {
  const { setToast } = useContext(AppContext);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [isExperimental, setIsExperimental] = useState(false);
  const [apiError, setApiError] = useState('');
  const glynt = useGlynt();
  const client = useQueryClient();

  // Reset form when dialog opens
  useEffect(() => {
    if (open && extractionBatch) {
      setLabel(extractionBatch.label || '');
      setIsExperimental(extractionBatch.is_experimental || false);
      setApiError('');
    }
  }, [open, extractionBatch]);

  const { data, isPending } = useQuery({
    queryKey: ['extractionBatches'],

    queryFn: async () => {
      return (await glynt).extractionBatch.list({ limit: 100 });
    },

    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false
  });

  const updateExtractionBatch = useMutation({
    mutationFn: async () => {
      if (!extractionBatch) return;

      const updates: { label?: string; is_experimental?: boolean } = {};

      if (label !== extractionBatch.label) {
        updates.label = removeTrailingSpaces(label);
      }
      if (isExperimental !== extractionBatch.is_experimental) {
        updates.is_experimental = isExperimental;
      }

      if (Object.keys(updates).length === 0) return;

      return (await glynt).extractionBatch.update(extractionBatch.id, updates);
    },

    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['extractionBatch', extractionBatch?.id]
      });
      client.invalidateQueries({
        queryKey: ['extractionBatches']
      });
      setOpen(false);
    },

    onError: (error: AxiosError) => {
      let message =
        error.message || 'Sorry, failed to update. Please make sure that the label is unique.';
      setToast({
        variant: 'error',
        open: true,
        message,
      });
      setApiError(message);
    }
  });

  const handleSubmit = () => {
    updateExtractionBatch.mutate();
  };

  const handleClose = () => {
    setOpen(false);
    setApiError('');
  };

  if (!extractionBatch?.label) {
    return null;
  }

  const existingNames = data?.results
    .filter((eb) => eb.id !== extractionBatch.id)
    .map((eb) => eb?.label) || [];

  const isDuplicate = existingNames.includes(label);
  const isLabelTooLong = label.length > LABEL_LIMIT;
  const isLabelEmpty = label.trim().length === 0;
  const hasChanges = label !== extractionBatch.label || isExperimental !== extractionBatch.is_experimental;

  const isSubmitDisabled =
    updateExtractionBatch.isPending ||
    isDuplicate ||
    isLabelTooLong ||
    isLabelEmpty ||
    !hasChanges;

  return (
    <>
      <Tooltip placement="top" title="Batch Properties">
        <CreateIcon
          style={{
            color: '#65BBA9',
            cursor: isPending ? 'not-allowed' : 'pointer',
            fontSize: '17px',
            marginLeft: '10px',
          }}
          data-cy="btn-rename-extraction-batch"
          onClick={() => setOpen(true)}
        />
      </Tooltip>
      <DialogGlynt
        open={open}
        closeModal={handleClose}
        cypressId="batch-properties-dialog"
        width="500px"
        loading={updateExtractionBatch.isPending}
      >
        <DialogTitleGlynt
          title="Batch Properties"
          closeModal={handleClose}
          loading={updateExtractionBatch.isPending}
        />

        <DialogContentGlynt>
          <FormControlStyled fullWidth>
            <FormLabelStyled>Batch Name</FormLabelStyled>
            <TextField
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Enter batch name"
              fullWidth
              variant="outlined"
              size="small"
              error={isDuplicate || isLabelTooLong || isLabelEmpty}
              helperText={
                isDuplicate
                  ? 'A batch with this name already exists'
                  : isLabelTooLong
                  ? `Name must be less than ${LABEL_LIMIT} characters`
                  : isLabelEmpty
                  ? 'Name is required'
                  : ''
              }
              disabled={updateExtractionBatch.isPending}
              data-cy="input-rename-extraction-batch"
            />
          </FormControlStyled>

          <FormControlStyled fullWidth>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isExperimental}
                  onChange={(e) => setIsExperimental(e.target.checked)}
                  disabled={updateExtractionBatch.isPending}
                  data-cy="checkbox-experimental"
                />
              }
              label="Experimental"
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 4 }}>
              Mark this batch as experimental to indicate it's for testing purposes
            </Typography>
          </FormControlStyled>

          {apiError && (
            <Typography color="error" sx={{ mt: 2 }}>
              {apiError}
            </Typography>
          )}
        </DialogContentGlynt>

        <DialogActionsGlynt
          handleSubmit={handleSubmit}
          closeModal={handleClose}
          cancelCypressId="btn-dialog-field-cancel"
          submitCypressId="btn-dialog-field-submit"
          clearCypressId="btn-dialog-field-clear"
          submitButtonText="Save"
          submitLoading={updateExtractionBatch.isPending}
          submitDisabled={isSubmitDisabled}
        />
      </DialogGlynt>
    </>
  );
}

export default ExtractionBatchRename;
